import apiClient from "./api-client";
import { handleApiError } from "./error-handler";

/**
 * Profile fields editable on the InternalUser entity (1:1 with User where Type=Internal).
 * All fields optional — partial update is supported.
 */
export interface InternalUserProfile {
  userId: number;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  roles: string[];
  phoneNumber?: string | null;
  jobTitle?: string | null;
  schedulingUrlClientFr?: string | null;
  schedulingUrlClientEn?: string | null;
  schedulingUrlTalentFr?: string | null;
  schedulingUrlTalentEn?: string | null;
}

export interface UpdateInternalUserProfileRequest {
  phoneNumber?: string | null;
  jobTitle?: string | null;
  schedulingUrlClientFr?: string | null;
  schedulingUrlClientEn?: string | null;
  schedulingUrlTalentFr?: string | null;
  schedulingUrlTalentEn?: string | null;
}

export interface InternalUserListItem {
  userId: number;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  roles: string[];
}

/**
 * Returns the profile of the currently authenticated internal user.
 * Available to all internal users (CSM/TAM/Admin).
 */
export const getMyInternalUserProfile = async (): Promise<InternalUserProfile> => {
  try {
    const response = await apiClient.get<InternalUserProfile>("/internal-users/me");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to load my internal user profile");
  }
};

/**
 * Returns the profile for a given user. Backend allows: self OR admin.
 */
export const getInternalUserProfile = async (
  userId: number
): Promise<InternalUserProfile> => {
  try {
    const response = await apiClient.get<InternalUserProfile>(
      `/internal-users/${userId}`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to load internal user profile");
  }
};

/**
 * Updates the profile for a given user. Backend allows: self OR admin.
 */
export const updateInternalUserProfile = async (
  userId: number,
  data: UpdateInternalUserProfileRequest
): Promise<void> => {
  try {
    await apiClient.put(`/internal-users/${userId}`, data);
  } catch (error) {
    throw handleApiError(error, "Failed to update internal user profile");
  }
};

/**
 * Returns the list of all internal users. Admin only.
 * Used by the profile page user picker so an admin can pick whose profile to edit.
 */
export const listInternalUsers = async (): Promise<InternalUserListItem[]> => {
  try {
    const response = await apiClient.get<InternalUserListItem[]>("/internal-users");
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to list internal users");
  }
};
